import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import Button from "./button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Body } from "./typography";
import CaretIcon from "./icons/caret";
import { MultiCheckbox } from "./checkbox";
import { Option } from "./checkbox";

type LabelComponentProps = {
  option: string;
};

interface SingleFilterProps {
  name: string;
  options: string[];
  value: string;
  onValueChange: (value: string) => void;
  contentWidth?: string;
}

interface MultiFilterProps {
  name: string;
  options: string[];
  values: string[];
  onValuesChange: (values: string[]) => void;
  contentWidth?: string;
}

const SingleSelectFilter = (props: SingleFilterProps) => {
  const { name, options, value, onValueChange } = props;

  const displayText = value ? `${name} (${value})` : name;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size="medium" className="inline-flex items-center justify-between gap-1">
          <div className="flex items-center justify-center gap-1 w-full">
            {displayText}
            <CaretIcon size="sm" color="green" />
          </div>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" sideOffset={0} className="pt-4 pb-6 px-6 ">
        <div className="">
          <RadioGroup value={value} onValueChange={onValueChange} className="flex flex-col gap-2">
            {options.map((option) => (
              <div key={option} className="flex items-center space-x-2 w-[197px]">
                <RadioGroupItem value={option} id={option} />
                <label htmlFor={option} className="cursor-pointer w-full">
                  <Body secondary style={{ lineHeight: "24px" }}>
                    {option}
                  </Body>
                </label>
              </div>
            ))}
          </RadioGroup>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const MultiSelectFilter = (props: MultiFilterProps) => {
  const { name, options, values, onValuesChange } = props;

  const DefaultMultiSelectLabel = ({ option }: LabelComponentProps) => (
    <Body secondary style={{ lineHeight: "24px" }}>
      {option}
    </Body>
  );

  const checkboxOptions = options.map((option) => ({
    label: <DefaultMultiSelectLabel option={option} />,
    value: option,
  }));

  const handleSelect = (selected: Option | Option[]) => {
    if (Array.isArray(selected) && onValuesChange) {
      const selectedValues = selected.map((option) => option.value as string);
      onValuesChange(selectedValues);
    }
  };

  const getDisplayText = () => {
    if (values.length === 0) return name;
    if (values.length === 1) return `${name} (${values[0]})`;
    return `${name} (${values.length})`;
  };

  const contentWidth = props.contentWidth ? `${props.contentWidth}px` : "197px";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size="medium" className="inline-flex items-center justify-between gap-1">
          <div className="flex items-center justify-center gap-1 w-full">
            {getDisplayText()}
            <CaretIcon size="sm" color="green" />
          </div>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" sideOffset={0} className="pt-4 pb-6 px-6 ">
        <div style={{ width: contentWidth }}>
          <MultiCheckbox
            id={`${name.toLowerCase().replace(/\s+/g, "-")}-filter`}
            options={checkboxOptions}
            isMulti={true}
            onSelect={handleSelect}
            values={values}
          />
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export { SingleSelectFilter, MultiSelectFilter };
